import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Eye, EyeOff, Lock, Mail } from "lucide-react";
import { useAuth } from "../hooks/useAuth";
import { useAuthStore } from "../stores/authStore";
import { getResetPasswordErrorMessage } from "../utils/authErrors";
import "./CSS/auth.css";
import cael from "./CSS/cael.png";

const ResetPassword = () => {
  const { t } = useTranslation("auth");
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { resetPassword, isResettingPassword } = useAuth();
  const logout = useAuthStore((state) => state.logout);
  const clearedSessionRef = useRef(false);

  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const stateEmail = location.state?.email ?? "";
    const paramEmail = searchParams.get("email") ?? "";
    const paramCode = searchParams.get("code") ?? "";
    if (stateEmail) setEmail(stateEmail);
    else if (paramEmail) setEmail(decodeURIComponent(paramEmail));
    if (paramCode) setCode(paramCode.trim());

    // Stale session (e.g. Google login) must not block this page via GuestRoute redirect
    if ((paramEmail || stateEmail) && !clearedSessionRef.current) {
      clearedSessionRef.current = true;
      logout(false);
    }
  }, [location.state, searchParams, logout]);

  useEffect(() => {
    if (!success) return undefined;
    const timer = setTimeout(() => navigate("/login", { replace: true }), 2500);
    return () => clearTimeout(timer);
  }, [success, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (password.length < 6) {
      setError(t("resetPassword.errors.passwordTooShort"));
      return;
    }
    if (password !== confirmPassword) {
      setError(t("resetPassword.errors.passwordsNotMatch"));
      return;
    }
    if (!code.trim()) {
      setError(t("resetPassword.errors.codeRequired"));
      return;
    }

    try {
      const result = await resetPassword({
        email: email.trim(),
        code: code.trim(),
        newPassword: password,
      });
      if (result.status === "success") {
        setSuccess(true);
      } else {
        setError(result.message || t("resetPassword.errors.failed"));
      }
    } catch (err) {
      setError(getResetPasswordErrorMessage(err, t));
    }
  };

  return (
    <div className="auth-wrapper">
      <div className="auth-bg"></div>

      <div className="auth-card">
        <Link to="/" className="auth-logo-link">
          <img className="auth-logo" src={cael} alt="St Cael Vog" />
        </Link>

        {success ? (
          <div className="auth-success">
            <div className="auth-success-icon">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="64"
                height="64"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                <polyline points="22 4 12 14.01 9 11.01"></polyline>
              </svg>
            </div>
            <h2>{t("resetPassword.success.title")}</h2>
            <p className="auth-success-text">{t("resetPassword.success.message")}</p>
            <p className="auth-hint">{t("resetPassword.success.redirecting")}</p>
          </div>
        ) : (
          <>
            <div className="auth-icon">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="48"
                height="48"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
              </svg>
            </div>

            <h2>{t("resetPassword.title")}</h2>
            <p className="auth-description">{t("resetPassword.subtitle")}</p>

            {error && <div className="auth-error">{error}</div>}

            <form className="auth-form" onSubmit={handleSubmit}>
              <div className="auth-input-wrapper">
                <Mail size={18} className="auth-input-icon" />
                <input
                  type="email"
                  className="auth-input"
                  placeholder={t("resetPassword.emailPlaceholder")}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  required
                />
              </div>

              <input
                type="text"
                className="auth-input"
                placeholder={t("resetPassword.codePlaceholder")}
                value={code}
                onChange={(e) => setCode(e.target.value.trim())}
                maxLength={6}
                inputMode="numeric"
                autoComplete="one-time-code"
                required
              />

              <div className="auth-input-wrapper">
                <Lock size={18} className="auth-input-icon" />
                <input
                  type={showPassword ? "text" : "password"}
                  className="auth-input"
                  placeholder={t("resetPassword.passwordPlaceholder")}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  minLength={6}
                  autoComplete="new-password"
                  required
                />
                <button
                  type="button"
                  className="auth-password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={
                    showPassword
                      ? t("login.hidePassword", { defaultValue: "Hide password" })
                      : t("login.showPassword", { defaultValue: "Show password" })
                  }
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>

              <div className="auth-input-wrapper">
                <Lock size={18} className="auth-input-icon" />
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  className="auth-input"
                  placeholder={t("resetPassword.confirmPasswordPlaceholder")}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  minLength={6}
                  autoComplete="new-password"
                  required
                />
                <button
                  type="button"
                  className="auth-password-toggle"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  aria-label={
                    showConfirmPassword
                      ? t("login.hidePassword", { defaultValue: "Hide password" })
                      : t("login.showPassword", { defaultValue: "Show password" })
                  }
                >
                  {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>

              <div className="auth-form-submit-group">
                <button
                  type="submit"
                  className="auth-submit-btn"
                  disabled={isResettingPassword}
                >
                  {isResettingPassword ? t("resetPassword.submitting") : t("resetPassword.submit")}
                </button>
                <Link
                  to="/forgot-password"
                  state={{ email }}
                  className="auth-text-link"
                >
                  {t("resetPassword.requestCode")}
                </Link>
              </div>
            </form>
          </>
        )}

        <Link to="/login" className="auth-back-link">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="19" y1="12" x2="5" y2="12"></line>
            <polyline points="12 19 5 12 12 5"></polyline>
          </svg>
          {t("resetPassword.backToLogin")}
        </Link>
      </div>
    </div>
  );
};

export default ResetPassword;
